import useAxios from "axios-hooks";
import AssignmentForm from "./AssignmentForm";
import { useParams } from "react-router-dom";
import ConfirmationButton from "../../components/button/ConfirmationButton";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";

export default function AssignmentSettings() {
  const params = useParams();

  const [{ data, loading, error }, getAssignment] = useAxios<any>(
    `/assignments/${params.assignmentId}`
  );

  const [
    {
      data: deleteAssigmentData,
      loading: deleteAssigmentLoading,
      error: deleteAssigmentError,
    },
    deleteAssignment,
  ] = useAxios<any, any>(
    {
      url: `/assignments/${params.assignmentId}`,
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    },
    { manual: true }
  );

  if (loading) {
    return <div>...</div>;
  }

  if (data.status === "ended") {
    return (
      <div className="flex flex-col gap-6">
        <div className="card">
          <div className="">
            <DeleteAssignment></DeleteAssignment>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="card">
        <div className="card-title">Assigment Settings</div>
        <div className="card-body bg-base-200 rounded-lg">
          <AssignmentForm
            onSuccess={undefined}
            defaultValues={data}
          ></AssignmentForm>
        </div>
      </div>
      <EndAssignment></EndAssignment>
      <StudentState></StudentState>
    </div>
  );
}

function DeleteAssignment() {
  const params = useParams();

  const [{ data, loading, error }, deleteAssignment] = useAxios<any>(
    {
      url: `/assignments/${params.assignmentId}`,
      method: "DELETE",
    },
    {
      manual: true,
    }
  );

  return (
    <div className="card ">
      <div className="card-title text-red-500">Delete Assignment</div>
      <div className="card-body  rounded-lg border border-error">
        {error && (
          <div className="text-red-500">
            <div>Could not delete assignment</div>
            <div>{error.response?.data?.message}</div>
          </div>
        )}
        {loading ? (
          <div className="">Deleting assignment...</div>
        ) : (
          <>
            <div className=" font-bold text-sm text-red-500">
              This assigment has moved to 'ended' by the instructor. The only
              allowed action now is to delete the assignment
            </div>
            <div className="mt-4">
              <ConfirmationButton
                id={"delete_assignment"}
                buttonText={"Delete Assignment"}
                body={`Are you sure you want to delete this assignment? This action is permanent and cannot be undone.`}
                title={"Delete Assignment"}
                onAccept={async () => {
                  try {
                    console.log("ok");
                    await deleteAssignment();
                    window.location.href = "/assignments";
                  } catch (error) {}
                }}
                onReject={() => {}}
                className={"btn btn-error btn-sm "}
                icon={<XCircleIcon className="h-4"></XCircleIcon>}
              ></ConfirmationButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EndAssignment() {
  const params = useParams();

  const [{ data, loading, error }, endAssignment] = useAxios<any>(
    {
      url: `/assignments/${params.assignmentId}/end`,
      method: "POST",
    },
    {
      manual: true,
    }
  );

  useEffect(() => {
    if (!loading) {
      if (data && data?.status === "ended") {
        window.location.reload();
      }
    }
  }, [data, loading, error]);

  return (
    <div className="card ">
      <div className="card-title text-red-500">End Assignment</div>
      <div className="card-body  rounded-lg border border-error">
        {data && (
          <div className="text-green-500">Assignment ended successfully!</div>
        )}
        {error && (
          <div className="text-red-500">
            <div>Could not reset student state!</div>
            <div>{error.response?.data?.message}</div>
          </div>
        )}
        {loading ? (
          <div className="">Changing assignment status.</div>
        ) : (
          <>
            <div className=" font-bold text-sm text-red-500">
              Ending this assignment will lock all student progress, preventing
              any further changes. Once ended, students will no longer be able
              to update their work, and any course or lab completions that occur
              afterward will not be reflected in the assignment's status. This
              action is permanent and cannot be undone, so please make sure you
              want to end the assignment before continuing.
            </div>
            <div className="mt-4">
              <ConfirmationButton
                id={"end_assignment"}
                buttonText={"End Assignment"}
                body={`Ending this assignment will lock all student progress, preventing any further changes. Once ended, students will no longer be able to update their work, and any course or lab completions that occur afterward will not be reflected in the assignment's status. This action is permanent and cannot be undone, so please make sure you want to end the assignment before continuing.`}
                title={"End Assignment"}
                onAccept={async () => {
                  try {
                    console.log("ok");
                    await endAssignment();
                  } catch (error) {}
                }}
                onReject={() => {}}
                className={"btn btn-error btn-sm "}
                icon={<XCircleIcon className="h-4"></XCircleIcon>}
              ></ConfirmationButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StudentState() {
  const params = useParams();

  const [{ data, loading, error }, resetState] = useAxios<any>(
    {
      url: `/assignments/${params.assignmentId}/reset-state`,
      method: "POST",
    },
    {
      manual: true,
    }
  );

  return (
    <div className="card ">
      <div className="card-title text-red-500">Student State</div>
      <div className="card-body  rounded-lg border border-error">
        {data && (
          <div className="text-green-500">
            Student state reset successfully!
          </div>
        )}
        {error && (
          <div className="text-red-500">
            <div>Could not reset student state!</div>
            <div>{error.response?.data?.message}</div>
          </div>
        )}
        {loading ? (
          <div className="">Reseting student state please wait.</div>
        ) : (
          <>
            <div className=" font-bold text-sm text-red-500">
              Resetting the assigned student’s state will erase their current
              progress for the selected courses and labs in this assignment.
              This option exists because students may have already viewed
              content or completed labs, causing their progress to appear as
              finished—even if the assignment hasn’t officially started.
            </div>
            <div className="mt-4">
              <ConfirmationButton
                id={"reset_student_state"}
                buttonText={"RESET STUDENT STATE"}
                body={`Resetting the assigned student’s state will erase their current progress for the selected courses and labs in this assignment. Are you sure you want to continue?`}
                title={"Reset Student State"}
                onAccept={async () => {
                  try {
                    console.log("ok");
                    await resetState();
                  } catch (error) {}
                }}
                onReject={() => {}}
                className={"btn btn-error btn-sm "}
                icon={<XCircleIcon className="h-4"></XCircleIcon>}
              ></ConfirmationButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
